'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  CalendarDays,
  BatteryFull,
  Check,
  CheckCircle2,
  Clock3,
  Hand,
  Signal,
  Wifi,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePrefersReducedMotion } from '@/components/sections/landing-motion';

type FeatureStep = {
  eyebrow: string;
  title: string;
  benefit: string;
  visual: ReactNode;
};

function PhoneStatusBar() {
  return (
    <div className="relative mb-3 flex h-7 items-start justify-between px-1 pt-1 text-[0.46rem] font-bold text-text-primary">
      <span>9:41</span>
      <span className="absolute left-1/2 top-0 h-4 w-[3.25rem] -translate-x-1/2 rounded-full bg-[#111]" aria-hidden="true" />
      <span className="flex items-center gap-1" aria-label="Señal, wifi y batería completos">
        <Signal size={10} strokeWidth={2.6} />
        <Wifi size={10} strokeWidth={2.6} />
        <BatteryFull size={12} strokeWidth={2.4} />
      </span>
    </div>
  );
}

function BookingVisual() {
  return (
    <div className="relative min-h-[17rem] overflow-hidden rounded-xl bg-[radial-gradient(circle_at_top_right,rgb(var(--color-accent)/0.18),transparent_45%)] p-3">
      <div className="absolute left-3 top-[7.5rem] z-10 max-w-[9.5rem] rounded-xl border border-border bg-bg-primary p-2.5 shadow-lg">
        <div className="flex items-center gap-2 text-[0.58rem] font-semibold text-text-primary">
          <span className="grid h-6 w-6 place-items-center rounded-full bg-success text-white"><Check size={13} /></span>
          Nueva reserva
        </div>
        <p className="mt-1 text-[0.52rem] leading-4 text-text-secondary">Corte de cabello con María<br />el 24 may a las 12:00</p>
      </div>
      <div className="absolute right-3 top-3 h-[20.5rem] w-[68%] overflow-hidden rounded-t-[2rem] border-x-[6px] border-t-[6px] border-[#151515] bg-bg-primary p-2 shadow-[0_20px_38px_rgb(0_0_0/0.24)]">
        <PhoneStatusBar />
        <p className="text-center text-[0.58rem] font-bold">Nueva reserva</p>
        <div className="mt-3 grid gap-2 text-[0.5rem] text-text-secondary">
          {['Corte de cabello', 'María Fernández', 'Viernes, 24 de mayo'].map((item) => <div key={item} className="rounded-md border border-border px-2 py-1.5">{item}</div>)}
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1 text-center text-[0.48rem]">
          {['09:00', '10:00', '11:00', '12:00', '13:00', '14:00'].map((time) => <span key={time} className={cn('rounded border border-border py-1', time === '12:00' && 'border-accent bg-accent text-bg-primary')}>{time}</span>)}
        </div>
        <div className="mt-2 rounded-md bg-accent py-2 text-center text-[0.52rem] font-bold text-bg-primary">Confirmar reserva</div>
      </div>
    </div>
  );
}

function AgendaVisual() {
  const appointments = [
    ['09:00', 'Ana López', 'Corte de cabello', 'bg-accent/12'],
    ['11:00', 'Diego Ramírez', 'Barba + cejas', 'bg-blue-500/10'],
    ['15:00', 'Sofía Martínez', 'Coloración', 'bg-violet-500/10'],
  ];
  return (
    <div className="relative h-[16rem] overflow-hidden rounded-xl bg-bg-secondary/65 px-5 pt-3">
      <div className="absolute inset-x-5 top-3 h-[20rem] overflow-hidden rounded-t-[2rem] border-x-[6px] border-t-[6px] border-[#151515] bg-bg-primary p-3 shadow-[0_20px_38px_rgb(0_0_0/0.24)]">
        <PhoneStatusBar />
        <div className="flex items-center justify-between text-[0.62rem] font-bold"><CalendarDays size={15} className="text-accent" />Viernes, 24 de mayo<span className="grid h-6 w-6 place-items-center rounded-full bg-accent text-bg-primary">+</span></div>
        <div className="mt-4 space-y-3">
          {appointments.map(([time, name, service, color]) => <div key={time} className="grid grid-cols-[2.5rem_1fr] gap-2 text-[0.52rem]"><span className="pt-2 text-text-secondary">{time}</span><div className={cn('rounded-lg border border-border p-2', color)}><strong className="block text-text-primary">{name}</strong><span className="text-text-secondary">{service}</span></div></div>)}
        </div>
      </div>
    </div>
  );
}

function ClientVisual() {
  return (
    <div className="mx-auto min-h-[15rem] max-w-[19rem] rounded-xl border border-border bg-bg-primary p-4 shadow-lg">
      <div className="flex items-center gap-3"><span className="grid h-11 w-11 place-items-center rounded-full bg-accent/18 font-bold text-accent">SM</span><div><strong className="block text-sm">Sofía Martínez</strong><span className="text-[0.58rem] text-text-secondary">+57 300 123 4567</span></div></div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-[0.55rem]"><div className="rounded-lg bg-bg-secondary p-2"><span className="text-text-secondary">Próxima cita</span><strong className="mt-1 block">24 may · 15:00</strong></div><div className="rounded-lg bg-bg-secondary p-2"><span className="text-text-secondary">Última visita</span><strong className="mt-1 block">4 may 2024</strong></div></div>
      <p className="mt-4 text-[0.6rem] font-bold">Historial de visitas</p>
      <div className="mt-2 border-l border-accent/50 pl-3 text-[0.55rem]">
        {[['4 may', 'Cepillado'], ['6 abr', 'Corte de cabello'], ['2 mar', 'Bigote + cejas']].map(([date, service]) => <div key={date} className="relative flex justify-between py-1.5 before:absolute before:-left-[0.95rem] before:top-3 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent"><span className="text-text-secondary">{date}</span><strong>{service}</strong></div>)}
      </div>
    </div>
  );
}

function AvailabilityVisual() {
  return (
    <div className="grid min-h-[13rem] grid-cols-[0.9fr_1.1fr] gap-2 text-[0.52rem]">
      <div className="rounded-xl border border-border bg-bg-primary p-3 shadow-md"><strong className="text-[0.65rem]">Registrar ausencia</strong>{['Desde 27 may 2024', 'Hasta 31 may 2024', 'Vacaciones'].map((item) => <div key={item} className="mt-2 rounded-md border border-border px-2 py-2 text-text-secondary">{item}</div>)}<div className="mt-2 rounded-md bg-accent py-2 text-center font-bold text-bg-primary">Guardar ausencia</div></div>
      <div className="rounded-xl border border-border bg-bg-primary p-3 shadow-md"><p className="text-center text-[0.65rem] font-bold">Mayo 2024</p><div className="mt-3 grid grid-cols-7 gap-1 text-center text-text-secondary">{Array.from({ length: 28 }, (_, index) => <span key={index} className={index > 21 ? 'rounded-full bg-accent py-1 text-bg-primary' : 'py-1'}>{index + 1}</span>)}</div><div className="mt-3 flex items-center gap-2 text-text-secondary"><span className="h-2 w-2 rounded-full bg-accent" />Fechas bloqueadas</div></div>
    </div>
  );
}

const steps: FeatureStep[] = [
  { eyebrow: 'Reservas online', title: 'Tus clientes reservan sin preguntarte cada horario', benefit: 'La cita aparece automáticamente en tu agenda', visual: <BookingVisual /> },
  { eyebrow: 'Agenda', title: 'Mira tu día completo de un vistazo', benefit: 'Sabes quién viene, a qué hora y qué servicio reservó', visual: <AgendaVisual /> },
  { eyebrow: 'Clientes', title: 'Cada cliente conserva su propia historia', benefit: 'No tienes que buscar información entre chats y cuadernos', visual: <ClientVisual /> },
  { eyebrow: 'Horarios y ausencias', title: 'Tu disponibilidad siempre se mantiene actualizada', benefit: 'Nadie puede reservar cuando no estás disponible', visual: <AvailabilityVisual /> },
];

function RevealStep({ step, index }: { step: FeatureStep; index: number }) {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const element = ref.current;
    if (!element || reducedMotion) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, [reducedMotion]);

  return (
    <article ref={ref} className={cn('relative pl-10 transition-all duration-700 ease-out', isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0')}>
      <span className="absolute left-0 top-3 z-10 grid h-8 w-8 place-items-center rounded-full border border-accent bg-bg-secondary font-heading text-sm font-bold text-accent">{String(index + 1).padStart(2, '0')}</span>
      <div className="overflow-hidden rounded-2xl border border-border bg-bg-primary p-4 shadow-mockup">
        <p className="text-[0.68rem] font-bold uppercase tracking-[0.1em] text-accent">{step.eyebrow}</p>
        <h3 className="mt-2 font-heading text-xl font-bold leading-tight text-text-primary">{step.title}</h3>
        <div className="mt-5">{step.visual}</div>
        <p className="mt-4 flex items-start gap-2 border-t border-border pt-3 text-xs leading-5 text-text-primary"><CheckCircle2 size={18} className="mt-0.5 shrink-0 text-accent" />{step.benefit}</p>
      </div>
    </article>
  );
}

export function MobileFeatureJourney() {
  return (
    <div className="mt-7 lg:hidden">
      <p className="flex items-center justify-center gap-2 text-sm font-medium text-accent"><Hand size={18} />Desliza para ver cómo funciona</p>
      <div className="relative mt-7 space-y-4 before:absolute before:bottom-8 before:left-[0.95rem] before:top-4 before:w-px before:bg-accent/45">
        {steps.map((step, index) => <RevealStep key={step.eyebrow} step={step} index={index} />)}
      </div>
      <div className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-border bg-bg-primary px-4 py-3 text-xs font-medium text-text-secondary"><Clock3 size={17} className="text-accent" />Todo conectado, sin información dispersa.</div>
    </div>
  );
}
